import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { ItemDeleteEvent } from '@autobid/ui/types/websocket/ItemDeleteEvent'
import type { ItemWithdrawEvent } from '@autobid/ui/types/websocket/ItemWithdrawEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { BAROMETER_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { useUpdateCar } from '@autobid/ui/composables/car/useUpdateCar'
import { getIdFromSlug } from '@autobid/ui/utils/car/getIdFromSlug'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { useQueryClient } from '@tanstack/vue-query'

export function useBarometerItemWithdrawOrDelete() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()
  const { updateBarometerCarData } = useUpdateCar()

  const itemWithdrawOrDelete = () => {
    const updateCar = (event: ItemWithdrawEvent | ItemDeleteEvent) => {
      const { itemId } = event.context

      updateBarometerCarData(undefined, itemId, (item) => {
        return {
          ...item,
          stage: 'IN_PREPARATION',
          publicationInfo: {
            ...item.publicationInfo,
            status: 'WITHDRAW',
          },
        }
      })
    }

    const invalidateCurrentCar = (
      event: ItemWithdrawEvent | ItemDeleteEvent,
    ) => {
      const { auctionId, itemId } = event.context
      const currentCarKey = getBarometerQueryKey({
        type: 'current',
        auctionId,
      })
      const currentData = queryClient.getQueryData(
        currentCarKey,
      ) as BarometerSuccess['data']['singleItem']

      if (!currentData)
        return

      const isItCurrentCar = currentData.item.itemData.id === itemId
      if (isItCurrentCar) {
        return queryClient.invalidateQueries(currentCarKey)
      }

      const nextCarId = getIdFromSlug(currentData.metadata.next || '')
      const prevCarId = getIdFromSlug(currentData.metadata.previous || '')

      const hasThisCarInPagination
        = itemId === nextCarId || itemId === prevCarId
      if (hasThisCarInPagination) {
        return queryClient.invalidateQueries(currentCarKey)
      }
    }

    /**
     * Invalidate cars that contains the withdrawn car in its metadata pagination
     */
    const invalidateCarsWithCarInPagination = (
      event: ItemWithdrawEvent | ItemDeleteEvent,
    ) => {
      const { itemId } = event.context

      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey))
            return false

          if (query.queryKey[0] !== 'barometer')
            return false

          const queryData = query.state
            .data as BarometerSuccess['data']['singleItem']

          if (!queryData)
            return false

          const nextCarId = getIdFromSlug(queryData.metadata.next || '')
          const prevCarId = getIdFromSlug(queryData.metadata.previous || '')

          const hasThisCarInPagination
            = itemId === nextCarId || itemId === prevCarId

          return hasThisCarInPagination
        },
      })
    }

    const callback = (event: ItemWithdrawEvent | ItemDeleteEvent) => {
      updateCar(event)
      randomDebounce(0, 2000, () => {
        invalidateCurrentCar(event)
        invalidateCarsWithCarInPagination(event)
      })
    }

    startListen({
      eventName: 'premium.ITEM_WITHDRAW',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: event => callback(event),
    })

    startListen({
      eventName: 'premium.ITEM_DELETED',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: event => callback(event),
    })
  }

  return { itemWithdrawOrDelete }
}
